// Shared Teach-Back state machine + capture contract.
//
// One machine, consumed by BOTH the standard renderer (Student) and the
// bespoke Lamplight skin, so a flagship can NEVER drift from the standard
// type's mechanic or capture — the exact class of bug the flagship audit
// found across the original 12 (CREATE-COMPOSITION-HANDOVER.md §8.1/§8.14).
// Presentation lives in the components; all behaviour lives here.

import { useState, useCallback, useMemo, useRef } from 'react';
import type {
  TeachBackSlideData,
  TeachBackCoachKind,
  TeachBackBranch,
} from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';

export type TeachBackOutcome = 'aha' | 'partial' | 'backfire' | 'dependency' | 'unresolved';
export type TeachBackPhase = 'redpen' | 'diagnose' | 'compose' | 'reply' | 'adapt' | 'residue' | 'done';

// Evaluate the branch rules: all present predicates AND; first match wins.
export function evalBranch(
  branches: TeachBackBranch[],
  composedKinds: TeachBackCoachKind[],
  opensWithKind: TeachBackCoachKind | undefined,
): TeachBackBranch | null {
  for (const b of branches) {
    const w = b.when;
    if (w.opensWithKind && w.opensWithKind !== opensWithKind) continue;
    if (w.includesAnyKind && !w.includesAnyKind.some((k) => composedKinds.includes(k))) continue;
    if (w.excludesAllKinds && w.excludesAllKinds.some((k) => composedKinds.includes(k))) continue;
    return b;
  }
  return null;
}

export interface TeachBackMachine {
  phase: TeachBackPhase;
  // Beat 1
  struck: Set<string>;
  toggleStrike: (id: string) => void;
  penDown: () => void;
  damageStruck: number;
  damageTotal: number;
  overStruck: boolean;
  // Beat 2
  diagnosisId: string | null;
  chooseDiagnosis: (id: string) => void;
  diagnosisCorrect: boolean;
  chosenDiagnosis: TeachBackSlideData['diagnosisOptions'][number] | undefined;
  // Beat 3
  composed: string[];
  addFragment: (id: string) => void;
  removeFragment: (id: string) => void;
  sayIt: () => void;
  // Beat 3→4
  outcome: TeachBackOutcome | null;
  studentReply: string;
  coachNote: string;
  chooseAdapt: (optId: string) => void;
  // Beat 5
  residueStruck: boolean;
  strikeResidue: () => void;
  residueCaught: boolean;
  approveRevision: () => void;
  adapted: boolean;
}

export function useTeachBack(
  data: TeachBackSlideData,
  cb: {
    onActivityResponse?: (response: ActivityResponsePayload) => void;
    onComplete?: () => void;
    savedResponse?: Record<string, unknown> | null;
  },
): TeachBackMachine {
  const { onActivityResponse, onComplete, savedResponse } = cb;
  const restored = !!savedResponse && typeof savedResponse.outcome === 'string';

  const [phase, setPhase] = useState<TeachBackPhase>(restored ? 'done' : 'redpen');
  const [struck, setStruck] = useState<Set<string>>(
    () => new Set(restored && Array.isArray(savedResponse!.struck) ? (savedResponse!.struck as string[]) : []),
  );
  const [diagnosisId, setDiagnosisId] = useState<string | null>(
    restored ? ((savedResponse!.diagnosisId as string) ?? null) : null,
  );
  const [composed, setComposed] = useState<string[]>(
    restored && Array.isArray(savedResponse!.coachingPath) ? (savedResponse!.coachingPath as string[]) : [],
  );
  const [outcome, setOutcome] = useState<TeachBackOutcome | null>(
    restored ? ((savedResponse!.outcome as TeachBackOutcome) ?? null) : null,
  );
  const [studentReply, setStudentReply] = useState<string>('');
  const [coachNote, setCoachNote] = useState<string>('');
  const [residueCaught, setResidueCaught] = useState<boolean>(restored ? !!savedResponse!.residueCaught : false);
  const [residueStruck, setResidueStruck] = useState<boolean>(restored ? !!savedResponse!.residueCaught : false);
  const adaptedRef = useRef<boolean>(restored ? !!savedResponse!.adapted : false);
  const completedRef = useRef(restored);

  const damageFragments = useMemo(() => data.artifact.fragments.filter((f) => f.damage), [data.artifact.fragments]);
  const damageStruck = damageFragments.filter((f) => struck.has(f.id)).length;
  const overStruck = data.artifact.fragments.some((f) => f.loadBearing && struck.has(f.id));

  const kindOf = useCallback(
    (id: string): TeachBackCoachKind | undefined => data.coachingFragments.find((f) => f.id === id)?.kind,
    [data.coachingFragments],
  );

  const fireCapture = useCallback(
    (finalOutcome: TeachBackOutcome, didAdapt: boolean, caughtResidue: boolean) => {
      const diagnosisCorrect = diagnosisId === data.misconceptionId;
      const isCorrect = diagnosisCorrect && finalOutcome === 'aha' && caughtResidue && !overStruck;
      const penScore = damageFragments.length ? (damageStruck / damageFragments.length) * (overStruck ? 0.6 : 1) : 1;
      const score = Math.round(
        penScore * 25 + (diagnosisCorrect ? 25 : 0) + (finalOutcome === 'aha' ? 25 : 0) + (caughtResidue ? 25 : 0),
      );
      onActivityResponse?.({
        activityType: 'teach-back',
        mode: 'quiz',
        responseData: {
          struck: Array.from(struck),
          overStruck,
          diagnosisId,
          diagnosisCorrect,
          coachingPath: composed,
          outcome: finalOutcome,
          adapted: didAdapt,
          residueCaught: caughtResidue,
        },
        isCorrect,
        score,
      });
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    },
    [diagnosisId, data.misconceptionId, overStruck, damageFragments.length, damageStruck, struck, composed, onActivityResponse, onComplete],
  );

  const toggleStrike = useCallback(
    (id: string) =>
      setStruck((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );

  const penDown = useCallback(() => setPhase('diagnose'), []);

  const chooseDiagnosis = useCallback((id: string) => {
    setDiagnosisId(id);
    setPhase('compose');
  }, []);

  const addFragment = useCallback(
    (id: string) => setComposed((prev) => (prev.includes(id) || prev.length >= data.maxCoaching ? prev : [...prev, id])),
    [data.maxCoaching],
  );
  const removeFragment = useCallback((id: string) => setComposed((prev) => prev.filter((x) => x !== id)), []);

  const sayIt = useCallback(() => {
    const kinds = composed.map((id) => kindOf(id)).filter(Boolean) as TeachBackCoachKind[];
    const opensWith = kindOf(composed[0] ?? '');
    const branch = evalBranch(data.branches, kinds, opensWith);
    const resolved = branch ?? { ...data.defaultBranch, id: '__default__', when: {}, allowsAdapt: false };
    setStudentReply(resolved.studentReply);
    setCoachNote(resolved.coachNote);
    setOutcome(resolved.outcome);
    setPhase(resolved.allowsAdapt && data.adaptBeat ? 'adapt' : 'residue');
  }, [composed, kindOf, data.branches, data.defaultBranch, data.adaptBeat]);

  const chooseAdapt = useCallback(
    (optId: string) => {
      const opt = data.adaptBeat?.options.find((o) => o.id === optId);
      if (!opt) return;
      adaptedRef.current = true;
      setStudentReply(opt.studentReply);
      setCoachNote(opt.coachNote);
      setOutcome(opt.leadsTo === 'aha' ? 'aha' : 'unresolved');
      setPhase('residue');
    },
    [data.adaptBeat],
  );

  const strikeResidue = useCallback(() => setResidueStruck((v) => !v), []);

  const approveRevision = useCallback(() => {
    const caught = residueStruck;
    setResidueCaught(caught);
    fireCapture(outcome ?? 'partial', adaptedRef.current, caught);
    setPhase('done');
  }, [residueStruck, outcome, fireCapture]);

  return {
    phase,
    struck,
    toggleStrike,
    penDown,
    damageStruck,
    damageTotal: damageFragments.length,
    overStruck,
    diagnosisId,
    chooseDiagnosis,
    diagnosisCorrect: diagnosisId === data.misconceptionId,
    chosenDiagnosis: data.diagnosisOptions.find((o) => o.id === diagnosisId),
    composed,
    addFragment,
    removeFragment,
    sayIt,
    outcome,
    studentReply,
    coachNote,
    chooseAdapt,
    residueStruck,
    strikeResidue,
    residueCaught,
    approveRevision,
    adapted: adaptedRef.current,
  };
}
